import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageHero } from '../components/PageHero';
import { CtaBand } from '../components/CtaBand';
import { Reveal } from '../components/Reveal';
import { usePageTitle } from '../hooks/usePageTitle';
import { FOUNDATION } from '../config/foundation';

const OBJECTS = [
  'Education &amp; training',
  'Prevention &amp; relief of poverty',
  'Health &amp; wellbeing',
  'Disability support',
  'Employment &amp; enterprise',
  'Equality &amp; inclusion',
];

const VALUES = [
  { title: 'Dignity first', desc: 'We meet people where they are, with respect and without judgement. Support is offered, never imposed.' },
  { title: 'Practical impact', desc: 'We focus on real outcomes — skills, income, and independence — not theory or box-ticking.' },
  { title: 'Lived experience', desc: 'Our frameworks are built from real lives and real breakthroughs, by people who have walked the path.' },
  { title: 'Accountability', desc: 'Structured sessions, micro-actions, and progress tracking keep momentum and trust between people.' },
  { title: 'Pay it forward', desc: 'Those we empower become mentors and advocates. Change is designed to spread, not stop.' },
  { title: 'Open to all', desc: 'From free tools to fully funded places, we work to remove the barriers that hold people back.' },
];

export function About() {
  usePageTitle('About Us');
  return (
    <Layout>
      <PageHero
        eyebrow="Our Story"
        title={<>About the{' '}<span className="text-mustard-gold">Foundation</span></>}
      >
        Born from lived experience, built with purpose &mdash; helping people reach their full potential.
      </PageHero>

      {/* Mission */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <Reveal className="max-w-3xl mx-auto text-center">
            <p className="eyebrow mb-4">Our Mission</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-display mb-6">
              <span className="text-gradient">Empowering people to become self-sustaining</span>
            </h2>
            <p className="text-slate-500 leading-relaxed text-base sm:text-lg mb-6">
              {FOUNDATION.name} is committed to empowering people to reach their full potential by
              providing practical opportunities to up-skill, leverage their skill sets, and ultimately
              become self-sustaining. Too often, capable people find themselves stuck &mdash; not for
              lack of ability, but for lack of opportunity, structure, and support.
            </p>
            <p className="text-slate-500 leading-relaxed text-base sm:text-lg">
              Our goal is that those we empower can, in turn, support and mentor others seeking to
              improve their lives. That ripple &mdash; from one person to the next &mdash; is at the
              heart of everything we do.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Rebrand note */}
      <section className="bg-white py-12 sm:py-16 border-t border-slate-100">
        <div className="section">
          <Reveal className="max-w-3xl mx-auto card p-6 sm:p-8 flex flex-col sm:flex-row gap-5 items-start">
            <div className="w-11 h-11 rounded-xl bg-mustard-gold/15 text-mustard-gold flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </div>
            <div>
              <h3 className="font-display font-bold text-mustard-green-dark mb-1">A new name, the same mission</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                We are proud to be rebranding from <strong>{FOUNDATION.formerName}</strong> to{' '}
                <strong>{FOUNDATION.name}</strong>. Our charitable purpose, registration, and the
                people we serve remain exactly the same &mdash; the new name simply reflects the
                programme through which we deliver our work: LifeSpark360&#8482;.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we do / objects */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <Reveal>
              <p className="eyebrow mb-3">Our Purpose</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-mustard-green-dark tracking-display mb-5">
                What we set out to do
              </h2>
              <p className="text-slate-500 leading-relaxed mb-4">
                Our work spans education and training, the prevention and relief of poverty and
                financial hardship, the advancement of health, support for people living with
                disability, and economic, community, and employment development.
              </p>
              <p className="text-slate-500 leading-relaxed">
                We also promote equality and diversity, and the social inclusion of those who are
                excluded from society &mdash; relieving need, developing capacity and skills, and
                supporting full participation in community life.
              </p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {OBJECTS.map((title, i) => (
                <Reveal key={title} delay={i * 80}>
                  <ObjectCard title={title} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founders / Trustees */}
      <section className="bg-white py-16 sm:py-24 border-t border-slate-100">
        <div className="section">
          <Reveal className="text-center mb-12 sm:mb-16">
            <p className="eyebrow mb-3">Our People</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-display">
              <span className="text-gradient">The founders behind the Foundation</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 max-w-5xl mx-auto">
            {/* Samuel */}
            <Reveal className="text-center">
              <div className="w-48 h-48 sm:w-56 sm:h-56 mx-auto mb-6 rounded-full overflow-hidden ring-4 ring-mustard-green-dark/10 ring-offset-4 ring-offset-white">
                <img
                  src="/samuel.odekunle.jpeg"
                  alt="Samuel Odekunle — Co-Founder & Trustee, The LifeSpark Foundation"
                  className="w-full h-full object-cover object-center scale-110"
                />
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-mustard-green-dark mb-1">
                Samuel Odekunle
              </h3>
              <p className="text-mustard-gold font-semibold text-sm mb-1">Co-Founder &amp; Trustee</p>
              <p className="text-slate-400 text-xs font-medium mb-5">{FOUNDATION.name}</p>
              <div className="h-px w-12 bg-mustard-gold/40 mx-auto mb-5" />
              <p className="text-slate-500 leading-relaxed text-sm text-left sm:text-center max-w-md mx-auto">
                Samuel is the architect of the LifeSpark360&#8482; programme and a Qualified Life
                Counsellor. With a career spanning leadership, strategy, and people development, he has
                dedicated himself to building frameworks that help people navigate transition with
                clarity and confidence. His belief that self-discovery is the foundation of lasting
                growth led to the creation of the SPARK Method.
              </p>
            </Reveal>

            {/* Itohan */}
            <Reveal delay={140} className="text-center">
              <div className="w-48 h-48 sm:w-56 sm:h-56 mx-auto mb-6 rounded-full overflow-hidden ring-4 ring-mustard-green-dark/10 ring-offset-4 ring-offset-white">
                <img
                  src="/itohan-odekunle.jpeg"
                  alt="Itohan (Itee) Odekunle — Founder, Trustee & Senior Coach, The LifeSpark Foundation"
                  className="w-full h-full object-cover object-center scale-110"
                />
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-bold text-mustard-green-dark mb-1">
                Itohan (Itee) Odekunle
              </h3>
              <p className="text-mustard-gold font-semibold text-sm mb-1">Founder, Trustee &amp; Senior Coach</p>
              <p className="text-slate-400 text-xs font-medium mb-5">{FOUNDATION.name}</p>
              <div className="h-px w-12 bg-mustard-gold/40 mx-auto mb-5" />
              <div className="text-slate-500 leading-relaxed text-sm text-left sm:text-center max-w-md mx-auto space-y-3">
                <p>
                  Itee is the founder and driving force behind the Foundation, and serves as Senior
                  LifeSpark360&#8482; Coach. Her unique combination of legal expertise and coaching passion
                  brings rigour, clarity, and genuine warmth to every interaction.
                </p>
                <p>
                  In her executive career, Itee serves as In-House Legal Counsel (EMEA) at
                  Wesco/Anixter, a global supply chain solutions company, advising on commercial
                  contracts, compliance, and new business opportunities. She sits on the company's
                  Inclusion and Diversity Across Borders Committee and leads professional development
                  for its BLIPOC business resource group.
                </p>
                <p>
                  Beyond her corporate role, she is one of the Vice Presidents of the Leeds Law
                  Society and a member of the Leeds Law School Advisory Board.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 sm:py-24">
        <div className="section">
          <Reveal className="text-center mb-12">
            <p className="eyebrow mb-3">What We Believe</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-mustard-green-dark tracking-display">
              Our principles
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 90}>
                <ValueCard title={v.title} desc={v.desc} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Governance */}
      <section className="bg-white py-16 sm:py-24 border-t border-slate-100">
        <div className="section">
          <Reveal className="max-w-3xl mx-auto text-center">
            <p className="eyebrow mb-4">Governance</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-mustard-green-dark tracking-display mb-6">
              A registered charity you can trust
            </h2>
            <p className="text-slate-500 leading-relaxed mb-6">
              {FOUNDATION.name} is a {FOUNDATION.structure} registered with the Charity Commission
              for England and Wales (Charity No. {FOUNDATION.charityNumber}), first registered on{' '}
              {FOUNDATION.registeredDate}. We operate throughout {FOUNDATION.jurisdictions} and are
              governed by a board of trustees under a comprehensive set of policies covering
              safeguarding, conduct, and financial controls.
            </p>
            <Link to="/policies" className="btn-secondary inline-block">View our policies</Link>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Be part of the spark"
        subtitle="Apply for a place on our programmes, or help us reach more people by giving your time, skills, or support."
      >
        <Link to="/apply" className="btn-gold text-center">Apply for Support</Link>
        <Link to="/get-involved" className="glass px-6 py-3 text-white rounded-xl font-semibold text-sm hover:bg-white/20 transition-all text-center">
          Get Involved
        </Link>
      </CtaBand>
    </Layout>
  );
}

function ObjectCard({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-100 bg-white hover:border-mustard-green-mid/40 transition-colors">
      <div className="w-5 h-5 rounded-full bg-mustard-green-dark/10 flex items-center justify-center flex-shrink-0">
        <svg className="w-3 h-3 text-mustard-green-dark" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
      </div>
      <span className="text-sm text-mustard-charcoal font-medium" dangerouslySetInnerHTML={{ __html: title }} />
    </div>
  );
}

function ValueCard({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="h-full p-6 rounded-2xl border border-slate-100 bg-white hover:border-mustard-green-mid/30 hover:shadow-card-hover transition-all">
      <h3 className="font-display font-bold text-mustard-green-dark mb-2 text-sm">{title}</h3>
      <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
    </div>
  );
}
